import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { Box, Modal } from "@mui/material";
import { toast } from "react-hot-toast";
import ModalCloseButton from "../../../components/Admin/ModalCloseButton";
import {
  useAddDashboardItemMutation,
  useDeleteDashboardItemMutation,
  useFetchAllDashboardItemsMutation,
} from "../../../features/admin/appUserDashboard/appUserDashboardApi";

const emptyForm = {
  name: "",
  action: "",
  link: "",
  order_at: "",
  icon: null,
};

const addModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: 560,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 8,
  p: 3,
  outline: "none",
};

const QuickUpdateDashboardItems = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [iconPreview, setIconPreview] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);

  const [
    fetchDashboardItems,
    { data: dashboardData, isLoading, error: fetchError },
  ] = useFetchAllDashboardItemsMutation();

  const [
    addDashboardItem,
    { data: addData, isLoading: isAdding, error: addError },
  ] = useAddDashboardItemMutation();
  const [
    deleteDashboardItem,
    { data: deleteData, isLoading: isDeleting, error: deleteError },
  ] = useDeleteDashboardItemMutation();

  const loadItems = () => {
    fetchDashboardItems({ token: sessionStorage.getItem("access_token") });
  };

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    if (dashboardData?.body) {
      setItems(dashboardData.body);
    }
  }, [dashboardData]);

  useEffect(() => {
    if (fetchError) {
      toast.error("Failed to load dashboard items");
    }
  }, [fetchError]);

  useMemo(() => {
    if (addData) {
      toast.success("Dashboard item added");
      setForm(emptyForm);
      setAddModalOpen(false);
      loadItems();
    }
    if (addError) {
      toast.error(addError?.data?.message || "Failed to add dashboard item");
    }
  }, [addData, addError]);

  useMemo(() => {
    if (deleteData) {
      toast.success("Dashboard item deleted");
      setDeletingItemId(null);
      loadItems();
    }
    if (deleteError) {
      setDeletingItemId(null);
      toast.error(deleteError?.data?.message || "Failed to delete dashboard item");
    }
  }, [deleteData, deleteError]);

  const filteredItems = items
    .filter((item) =>
      item.name?.toLowerCase().includes(search.trim().toLowerCase()),
    )
    .sort((a, b) => Number(a.order_at ?? 0) - Number(b.order_at ?? 0));

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "icon") {
      setForm((prev) => ({ ...prev, icon: files?.[0] || null }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!form.icon) {
      setIconPreview(null);
      return;
    }

    const previewUrl = URL.createObjectURL(form.icon);
    setIconPreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [form.icon]);

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    setForm(emptyForm);
    setIconPreview(null);
  };

  const handleAddItem = (e) => {
    e.preventDefault();

    if (
      !form.name ||
      !form.action ||
      !form.link ||
      !form.order_at ||
      !form.icon
    ) {
      toast.error("Please fill all fields including icon");
      return;
    }

    addDashboardItem({
      token: sessionStorage.getItem("access_token"),
      name: form.name.trim(),
      action: form.action.trim(),
      link: form.link.trim(),
      order_at: form.order_at,
      icon: form.icon,
    });
  };

  const handleDeleteItem = (item) => {
    if (isDeleting) return;

    const shouldDelete = window.confirm(
      `Delete "${item.name}" from dashboard items?`,
    );
    if (!shouldDelete) return;

    setDeletingItemId(item.id);
    deleteDashboardItem({
      token: sessionStorage.getItem("access_token"),
      id: item.id,
    });
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-3 pb-2 border-bottom">
        <div className="pr-3">
          <h5 className="mb-1 font-weight-bold text-primary">
            Dashboard Items
          </h5>
          <p className="text-muted small mb-0">
            Master list used when configuring client dashboards. Add new items
            here to make them available everywhere.
          </p>
        </div>
        <ModalCloseButton onClose={onClose} />
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: 320 }}
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-primary btn-sm px-3"
          onClick={() => setAddModalOpen(true)}
        >
          + Add Item
        </button>
      </div>

      <div
        className="border rounded p-2 bg-white"
        style={{ minHeight: 300, maxHeight: "52vh", overflowY: "auto", overflowX: "hidden" }}
      >
        {isLoading && (
          <div className="text-center py-5 text-muted">Loading items...</div>
        )}
        {!isLoading && filteredItems.length === 0 && (
          <div className="text-center py-5 text-muted">
            {search ? "No items match your search." : "No dashboard items yet."}
          </div>
        )}
        {!isLoading && filteredItems.length > 0 && (
          <div className="row">
            {filteredItems.map((item, idx) => (
              <div key={item.id} className="col-12 col-sm-6 col-lg-3 mb-3">
                <div className="card h-100 shadow-sm border-0" style={{ minHeight: 102, overflow: "hidden" }}>
                  <div className="card-body py-1 px-2 d-flex flex-column">
                    <div className="d-flex justify-content-end align-items-center">
                      <span className="badge badge-primary badge-pill">#{idx + 1}</span>
                    </div>
                    <div className="mt-2 d-flex align-items-start flex-grow-1">
                      <div
                        className="border rounded-circle bg-light d-flex align-items-center justify-content-center mr-2"
                        style={{ width: 34, height: 34, overflow: "hidden", flexShrink: 0 }}
                      >
                        {item.icon ? (
                          <img
                            src={item.icon}
                            alt={item.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <span className="small text-muted">N/A</span>
                        )}
                      </div>
                      <div className="w-100" style={{ minWidth: 0 }}>
                        <h6 className="card-title mb-1 font-weight-bold text-truncate" style={{ fontSize: "0.86rem" }}>
                          {item.name}
                        </h6>
                        <p className="card-text text-muted mb-0 text-truncate" style={{ lineHeight: "1.12", fontSize: "0.70rem" }}>
                          {item.action}
                        </p>
                        <p className="card-text text-muted mb-0 text-truncate" style={{ lineHeight: "1.12", fontSize: "0.70rem" }}>
                          /{item.link}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mt-1">
                      <button
                        type="button"
                        className="btn btn-link p-0 text-danger"
                        style={{ lineHeight: 1 }}
                        onClick={() => handleDeleteItem(item)}
                        disabled={isDeleting && deletingItemId === item.id}
                        title="Delete item"
                      >
                        {isDeleting && deletingItemId === item.id ? (
                          <span
                            className="spinner-border spinner-border-sm text-danger"
                            role="status"
                            aria-hidden="true"
                            title="Deleting..."
                          />
                        ) : (
                          <AiOutlineDelete size={15} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-muted small mt-2 mb-0">
        {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""}{" "}
        shown
      </p>

      <Modal open={addModalOpen} onClose={handleAddModalClose}>
        <Box sx={addModalStyle}>
          <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
            <h6 className="mb-0 font-weight-bold">Add Dashboard Item</h6>
            <ModalCloseButton onClose={handleAddModalClose} />
          </div>

          <form onSubmit={handleAddItem}>
            <div className="row">
              <div className="col-md-6 form-group mb-3">
                <label className="small font-weight-bold d-block mb-1">
                  Name *
                </label>
                <input
                  className="form-control"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  placeholder="Product Catalogue"
                />
              </div>
              <div className="col-md-6 form-group mb-3">
                <label className="small font-weight-bold d-block mb-1">
                  Action *
                </label>
                <input
                  className="form-control"
                  name="action"
                  value={form.action}
                  onChange={handleFormChange}
                  placeholder="product_catalogue"
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 form-group mb-3">
                <label className="small font-weight-bold d-block mb-1">
                  Link *
                </label>
                <input
                  className="form-control"
                  name="link"
                  value={form.link}
                  onChange={handleFormChange}
                  placeholder="product-catalogue"
                />
              </div>
              <div className="col-md-6 form-group mb-3">
                <label className="small font-weight-bold d-block mb-1">
                  Order *
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="order_at"
                  value={form.order_at}
                  onChange={handleFormChange}
                  min={1}
                />
              </div>
            </div>
            <div className="form-group mb-3">
              <label className="small font-weight-bold d-block mb-2">
                Icon *
              </label>
              <div className="border rounded p-3 bg-light">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <label
                      htmlFor="dashboard-item-icon"
                      className="btn btn-outline-primary btn-sm mb-0"
                    >
                      Choose Icon
                    </label>
                    <input
                      id="dashboard-item-icon"
                      type="file"
                      name="icon"
                      accept="image/*"
                      onChange={handleFormChange}
                      className="d-none"
                    />
                    <span
                      className="small text-muted text-truncate"
                      style={{ maxWidth: 190 }}
                    >
                      {form.icon?.name || "No file selected"}
                    </span>
                  </div>
                  <div
                    className="border rounded bg-white d-flex align-items-center justify-content-center ml-2"
                    style={{ width: 64, height: 64, flexShrink: 0, overflow: "hidden" }}
                  >
                    {iconPreview ? (
                      <img
                        src={iconPreview}
                        alt="Icon preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <span className="small text-muted">Preview</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 pt-2 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleAddModalClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm px-4"
                disabled={isAdding}
              >
                {isAdding ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default QuickUpdateDashboardItems;
